import { createContext, useContext } from 'react';
import { UNSAFE_LocationContext } from 'react-router-dom';

/** { open(options), close() } from the nearest EnquiryProvider, or null. */
export const EnquiryContext = createContext(null);

const warnNoProvider = () => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('useEnquiry(): no <EnquiryProvider> above this component. Use <EnquiryButton> or useEnquiryDialog(), which work without one.');
  }
};

const NO_PROVIDER = Object.freeze({ open: warnNoProvider, close: () => {}, available: false });

/**
 * The page-wide enquiry dialog: `const { open } = useEnquiry(); open({ role, planContext,
 * sourcePath })`. Needs an <EnquiryProvider> above (PublicLayout mounts one on the
 * redesigned site); without one it returns `{ available: false }` and `open` does nothing
 * but warn in development. Components that must also work without a provider use
 * <EnquiryButton> or useEnquiryDialog().
 */
export function useEnquiry() {
  const ctx = useContext(EnquiryContext);
  return ctx || NO_PROVIDER;
}

/**
 * The router's current pathname when rendered inside a router, else null (read from the
 * location context directly, so it never throws outside a router, e.g. in unit tests).
 */
export function useCurrentPath() {
  const loc = useContext(UNSAFE_LocationContext);
  return (loc && loc.location && loc.location.pathname) || null;
}
